import { LegacyRef, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
  Upload,
  UploadBody,
  UploadText,
  Textarea,
} from 'keep-react'

import { Wrapper } from 'components'
import Quill from 'quill'
import { Editor } from 'components/QuillEditor'
import { ArrowLeft, FloppyDisk, Trash } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { PageType, QA } from 'api/types/page'
import { pageService } from 'api/services/page'
import { sanitizeSlug } from 'utils/sanitizeString'

// form validation schema
const validationSchema = Yup.object().shape({
  categoryId: Yup.number(),
  status: Yup.number().oneOf([0, 1]),
  hasSimulation: Yup.boolean(),
  productFiles: Yup.mixed().nullable(),
})

export const BackofficePage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  //loaded page data
  const [data, setData] = useState<PageType | null>(null)

  // Quill States
  const [content, setContent] = useState<string>('')

  // Modal Control
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  // Save Button Active?
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // Files to upload
  const [file, setFile] = useState<File | null>(null)

  const [QAs, setQAs] = useState<QA[]>([])

  // Delete File Modal
  const [showDeleteFileModal, setShowDeleteFileModal] = useState<boolean>(false)

  // File to delete
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)

  // Editor Ref
  const quillContentRef = useRef(null)

  //get page details
  const getPage = async () => {
    setIsLoading(true)
    try {
      const response = (await pageService.getPage({
        id: Number(id),
      })) as PageType
      setData(response as PageType)
      setContent(response.content)
      setQAs(response.qAs || [])
      formik.setFieldValue('id', response.id)
      formik.setFieldValue('title', response.title)
      formik.setFieldValue('slug', response.slug)
      formik.setFieldValue('active', response.active)

      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar a página.')
    }
  }

  // using formik to handle form state
  const formik = useFormik({
    initialValues: {
      id: id,
      title: '',
      slug: '',
      active: false,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      if (!id) return
      const newValues = {
        id: values.id,
        title: values.title,
        slug: values.slug,
        content: content,
        active: values.active,
      } as PageType

      try {
        setIsApiBeingCalled(true)
        await pageService.savePage(newValues)

        toast.success('Página atualizada com sucesso.')
        setHasChanges(false)
      } catch (error) {
        toast.error('Erro ao atualizar página.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  const qaFormik = useFormik({
    initialValues: {
      question: '',
      answer: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      if (!data) return
      try {
        setIsApiBeingCalled(true)
        await pageService.addPageQAs({
          pageId: data.id!,
          question: values.question,
          answer: values.answer,
        })
        qaFormik.setFieldValue('question', '')
        qaFormik.setFieldValue('answer', '')
        getPage()
        toast.success('FAQ adicionada com sucesso.')
      } catch (error) {
        toast.error('Erro ao adicionar FAQ.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // formik for file upload
  const uploadFormik = useFormik({
    initialValues: {
      label: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async () => {
      if (!data || !file) return
      try {
        setIsApiBeingCalled(true)
        await pageService.addPageFiles({
          id: String(data.id),
          file: {
            label: uploadFormik.values.label,
            file: file,
          },
        })
        setFile(null)
        uploadFormik.setFieldValue('label', '')
        getPage()
        toast.success('Ficheiro(s) carregado(s) com sucesso.')
      } catch (error) {
        toast.error('Erro ao carregar ficheiro(s).')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // delete selected page
  const deletePage = async () => {
    if (!data) return
    try {
      await pageService.deletePage({
        id: Number(data.id),
      })
      setShowDeleteModal(false)
      toast.success('Página apagada com sucesso.')
      navigate(RouteNamesAuth.BACKOFFICE_PAGES)
    } catch (error) {
      toast.error('Não foi possível apagar a página.')
    }
  }

  // delete selected file
  const deleteFile = async () => {
    if (!data || !fileToDelete) return
    try {
      setIsApiBeingCalled(true)
      await pageService.removePageFile({
        pageId: String(data.id),
        fileId: fileToDelete,
      })
      setShowDeleteFileModal(false)
      toast.success('Ficheiro apagado com sucesso.')
      getPage()
    } catch (error) {
      toast.error('Não foi possível apagar o ficheiro.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    setFile(acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles[0] : null)
  }, [])

  const deleteQA = async (id: number) => {
    if (!data) return
    try {
      setIsApiBeingCalled(true)
      await pageService.removePageQA({
        pageId: String(data.id),
        qaId: String(id),
      })
      toast.success('FAQ apagada com sucesso.')
      getPage()
    } catch (error) {
      toast.error('Não foi possível apagar a FAQ.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  // initial data fetch
  useEffect(() => {
    getPage()
  }, [])

  // track changes to enable save button
  useEffect(() => {
    setHasChanges(true)
  }, [content, formik.values])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_PAGES)}
            >
              <ArrowLeft size={16} /> Páginas
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span></span>
                <>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      position="start"
                      color="secondary"
                      variant="default"
                      disabled={!hasChanges || isApiBeingCalled}
                      className="gap-2"
                      onClick={() => {
                        formik.handleSubmit()
                      }}
                    >
                      <FloppyDisk size={16} />
                      Gravar alterações
                    </Button>

                    <Button
                      position="end"
                      variant="default"
                      color="error"
                      onClick={() => {
                        setShowDeleteModal(true)
                      }}
                      disabled={isApiBeingCalled}
                      className="gap-2"
                    >
                      <Trash size={16} />
                      Apagar Página
                    </Button>
                  </ButtonGroup>
                </>
              </h1>

              <Tabs defaultValue="general">
                <TabsList>
                  <TabsItem value="general">Geral</TabsItem>
                  <TabsItem value="documents">Documentos</TabsItem>
                  <TabsItem value="faqs">FAQs</TabsItem>
                </TabsList>

                <TabsContent value="general" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Nome
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.title}
                        onChange={e => {
                          formik.setFieldValue('title', e.currentTarget.value)
                          formik.setFieldValue(
                            'slug',
                            sanitizeSlug(e.currentTarget.value),
                          )
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Slug
                      </h3>
                      <Input name="slug" value={formik.values.slug} disabled />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Conteúdo
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={content}
                          setContent={setContent}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Status
                      </h3>
                      <span className="flex items-center gap-2">
                        {data.active ? (
                          <Badge
                            className="pointer-events-none"
                            color="success"
                            variant="base"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            className="pointer-events-none"
                            color="error"
                            variant="base"
                          >
                            Inativo
                          </Badge>
                        )}
                        <Switch
                          checked={formik.values.active}
                          onCheckedChange={() => {
                            setHasChanges(true)
                            formik.setFieldValue(
                              'active',
                              !formik.values.active,
                            )
                          }}
                          className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                        />
                      </span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="p-4 md:p-6">
                  <>
                    {data.files && data.files.length === 0 && (
                      <p className="text-body-4">
                        Não há documentos disponíveis para este produto.
                      </p>
                    )}

                    {data.files && data.files.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Label</TableHead>
                            <TableHead>Ficheiro</TableHead>
                            <TableHead>Tipo de Conteúdo</TableHead>
                            <TableHead>Tamanho (bytes)</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.files.length > 0 &&
                            data.files.map(file => (
                              <TableRow key={file.id}>
                                <TableCell>{file.label}</TableCell>
                                <TableCell>
                                  <Button variant="link" color="primary">
                                    <Link to={file.uri} target="_blank">
                                      Ver Ficheiro
                                    </Link>
                                  </Button>
                                </TableCell>
                                <TableCell>{file.contentType}</TableCell>
                                <TableCell>{file.sizeBytes}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    color="error"
                                    className="gap-2"
                                    onClick={() => {
                                      setFileToDelete(file.id.toString())
                                      setShowDeleteFileModal(true)
                                    }}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}

                    <div className="pt-6">
                      <h3 className="text-xl text-primary-500 pb-4">
                        Adicionar documentos
                      </h3>
                      <form className="flex items-start gap-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Label
                            </h3>
                            <Input
                              type="text"
                              name="label"
                              onChange={e =>
                                uploadFormik.setFieldValue(
                                  'label',
                                  e.currentTarget.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Ficheiro
                            </h3>
                            <Upload
                              horizontal
                              options={{ onDrop, multiple: false }}
                              className="p-0 border-none"
                            >
                              {file ? (
                                <p className="text-body-4 font-semibold text-metal-600 flex items-center justify-between h-11">
                                  <span>{file.name}</span>
                                  <Button
                                    color="error"
                                    variant="link"
                                    onClick={() => setFile(null)}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </p>
                              ) : (
                                <UploadBody>
                                  <UploadText>
                                    <p className="text-body-4 font-medium text-metal-600">
                                      Escolha ou arraste o seu ficheiro aqui
                                      (PDF, JPG ou WEBP))
                                    </p>
                                  </UploadText>
                                </UploadBody>
                              )}
                            </Upload>
                          </div>
                        </div>
                        <div className="col-span-2 justify-self-end pt-8">
                          <Button
                            disabled={!file || !uploadFormik.values.label}
                            type="button"
                            onClick={() => uploadFormik.handleSubmit()}
                          >
                            Carregar Ficheiro
                          </Button>
                        </div>
                      </form>
                    </div>
                  </>
                </TabsContent>

                <TabsContent value="faqs" className="p-4 md:p-6">
                  <>
                    {QAs.length === 0 && (
                      <p className="text-body-4">
                        Não há FAQs disponíveis para esta página.
                      </p>
                    )}

                    {QAs.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Pergunta</TableHead>
                            <TableHead>Resposta</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {QAs.length > 0 &&
                            QAs.map((qa, index) => (
                              <TableRow key={index}>
                                <TableCell className="normal-case">
                                  {qa.question}
                                </TableCell>
                                <TableCell className="normal-case">
                                  {qa.answer}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    color="error"
                                    className="gap-2"
                                    onClick={() => {
                                      deleteQA(qa.id!)
                                    }}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}

                    <div className="mt-12 border border-metal-100 rounded-lg p-4">
                      <h3 className="text-body-3 font-medium pb-2 uppercase text-primary-500">
                        Adicionar FAQs
                      </h3>
                      <form onSubmit={qaFormik.handleSubmit}>
                        <div className="flex flex-col gap-4">
                          <Input
                            placeholder="Pergunta"
                            value={qaFormik.values.question}
                            onChange={qaFormik.handleChange}
                            name="question"
                          />
                          <Textarea
                            placeholder="Resposta"
                            value={qaFormik.values.answer}
                            onChange={qaFormik.handleChange}
                            name="answer"
                          />
                          <div className="flex items-center justify-end">
                            <Button
                              type="button"
                              onClick={() => qaFormik.handleSubmit()}
                            >
                              Adicionar FAQ
                            </Button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </>
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </Wrapper>

      {/* Delete Page Modal */}
      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Página</ModalTitle>
            <ModalDescription>
              Quer apagar esta página?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados desta página irão ser perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deletePage()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete File Modal */}
      <Modal open={showDeleteFileModal} onOpenChange={setShowDeleteFileModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Ficheiro</ModalTitle>
            <ModalDescription>
              Quer apagar este ficheiro?
              <br />
              <br />
              <strong className="text-error-500">
                Este ficheiro irá ser apagado.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteFileModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteFile()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
